import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, ChefHat, Bike, PartyPopper, UserCheck, ArrowLeft } from "lucide-react";

const STEPS = [
  { key: "received", icon: PackageCheck, tKey: "status_received" },
  { key: "preparing", icon: ChefHat, tKey: "status_preparing" },
  { key: "on_the_way", icon: Bike, tKey: "status_on_the_way" },
  { key: "delivered", icon: PartyPopper, tKey: "status_delivered" },
];

export default function Track() {
  const { t, lang } = useApp();
  const [num, setNum] = useState("");
  const [order, setOrder] = useState(null);
  const [customerOrders, setCustomerOrders] = useState(null); // list when customer_number is queried
  const [loading, setLoading] = useState(false);

  const search = async (overrideNum) => {
    const q = (overrideNum ?? num).trim().toUpperCase();
    if (!q) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track/${q}`);
      if (data.customer_orders) {
        setCustomerOrders(data.customer_orders);
        setOrder(null);
        if (data.customer_orders.length === 1) {
          // Single order under this customer → show it directly
          setOrder(data.customer_orders[0]);
          setCustomerOrders(null);
        }
      } else {
        setOrder(data);
        setCustomerOrders(null);
      }
    } catch (e) {
      setOrder(null);
      setCustomerOrders(null);
      toast.error(e.response?.data?.detail || "Sipariş bulunamadı");
    } finally {
      setLoading(false);
    }
  };

  const back = () => {
    setOrder(null);
  };

  const currentIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;
  const statusLabel = (st) => t(`status_${st}`) || st;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB"); }
    catch { return iso || ""; }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#1B2A22] mb-2">
          {t("track_title")}
        </h1>
        <p className="text-[#1B2A22]/60 mb-8">
          {lang === "tr"
            ? "Sipariş numarası (IKZ-...) veya müşteri numaranız (ör. 1001) ile sorgulayın."
            : "Enter your order number (IKZ-...) or your 4-digit customer number."}
        </p>

        <div className="flex gap-2 mb-8">
          <Input
            data-testid="track-input"
            value={num}
            onChange={(e) => setNum(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder={t("track_placeholder")}
            className="h-12 bg-white border-[#E8E2D5]"
          />
          <Button
            data-testid="track-btn"
            onClick={() => search()}
            disabled={loading}
            className="h-12 px-6 bg-[#2E5A36] hover:bg-[#1B2A22] rounded-full font-heading font-bold"
          >
            {t("track_btn")}
          </Button>
        </div>

        {/* Customer number → list of orders */}
        {customerOrders && !order && (
          <div data-testid="customer-orders-list" className="bg-white border border-[#E8E2D5] rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-[#2E5A36]">
              <UserCheck className="w-5 h-5" />
              <p className="font-heading font-bold">
                {lang === "tr"
                  ? `Bu müşteri numarasına ait ${customerOrders.length} sipariş bulundu`
                  : `${customerOrders.length} orders found for this customer`}
              </p>
            </div>
            <div className="divide-y divide-[#E8E2D5]">
              {customerOrders.map((o) => {
                const statusColor =
                  o.status === "delivered" ? "bg-[#2E5A36] text-white"
                  : o.status === "cancelled" ? "bg-red-500 text-white"
                  : o.status === "on_the_way" ? "bg-[#D9653B] text-white"
                  : "bg-[#E5A024] text-white";
                return (
                  <button
                    key={o.order_number}
                    data-testid={`customer-order-${o.order_number}`}
                    onClick={() => setOrder(o)}
                    className="w-full py-3 px-1 flex items-center justify-between gap-3 hover:bg-[#FAF7F2] rounded-lg text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-mono font-extrabold text-[#1B2A22]">{o.order_number}</p>
                      <p className="text-[11px] text-[#1B2A22]/60">{fmtDate(o.created_at)}</p>
                    </div>
                    <span className={`shrink-0 text-[10px] font-heading font-bold rounded-full px-3 py-1 ${statusColor}`}>
                      {statusLabel(o.status)}
                    </span>
                    <span className="shrink-0 font-mono font-bold text-[#2E5A36] w-24 text-right">
                      ₺{o.total.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Single order details */}
        {order && (
          <div data-testid="track-result" className="bg-white border border-[#E8E2D5] rounded-2xl p-6 space-y-6">
            {customerOrders && (
              <Button
                data-testid="track-back-btn"
                variant="outline"
                size="sm"
                onClick={back}
                className="rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {lang === "tr" ? "Siparişlerime Dön" : "Back to my orders"}
              </Button>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#2E5A36]">
                  {t("order_number")}
                </p>
                <p className="font-mono font-extrabold text-xl">{order.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-heading font-bold text-[#2E5A36]">
                  {t("total")}
                </p>
                <p className="font-mono font-extrabold text-xl text-[#2E5A36]">
                  ₺{order.total.toFixed(2)}
                </p>
              </div>
            </div>

            {order.status === "cancelled" ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 font-semibold">
                {t("status_cancelled")}
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-[#E8E2D5]" />
                <div className="space-y-6">
                  {STEPS.map((s, i) => {
                    const done = i <= currentIndex;
                    const active = i === currentIndex;
                    const Icon = s.icon;
                    return (
                      <div key={s.key} className="flex gap-4 relative">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 relative z-10 ${
                            done
                              ? "bg-[#2E5A36] text-[#FAF7F2]"
                              : "bg-white border-2 border-[#E8E2D5] text-[#E8E2D5]"
                          } ${active ? "ring-4 ring-[#2E5A36]/20 animate-pulse" : ""}`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="pt-2">
                          <p className={`font-heading font-bold ${done ? "text-[#1B2A22]" : "text-[#1B2A22]/40"}`}>
                            {t(s.tKey)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-[#E8E2D5]">
              <p className="text-xs font-heading font-bold uppercase tracking-wider text-[#2E5A36] mb-2">
                {lang === "tr" ? "Ürünler" : "Items"}
              </p>
              <ul className="space-y-1 text-sm">
                {order.items.map((i, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{i.quantity}× {i.name}</span>
                    <span className="font-mono">₺{(i.price * i.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
